/**
 * RS countdown theme patch — upgrade-safe enhancements for Prestige <countdown-timer>.
 * Keeps start-date support, avoids 00:00:00 flash on navigation, and guards flip updates.
 * Loaded after theme.js so Prestige upgrades do not wipe these behaviors.
 */
import { CountdownTimer, CountdownTimerFlip } from "theme";

const startDates = new WeakMap();
const patched = new WeakSet();

function pad(value) {
  return String(Math.min(99, Math.max(0, value))).padStart(2, "0");
}

function parseDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function refreshFlips(timer) {
  const flips = Array.from(timer.querySelectorAll("countdown-timer-flip"));
  return {
    days: flips.find((flip) => flip.getAttribute("type") === "days"),
    hours: flips.find((flip) => flip.getAttribute("type") === "hours"),
    minutes: flips.find((flip) => flip.getAttribute("type") === "minutes"),
    seconds: flips.find((flip) => flip.getAttribute("type") === "seconds"),
  };
}

function writeFlip(flip, value) {
  if (!flip || typeof flip.updateValue !== "function") return;
  flip.updateValue(value);
}

function applyRemaining(timer, endDate) {
  const now = Date.now();
  const end = endDate.getTime();
  if (end <= now) return;

  let remaining = Math.max(0, Math.floor((end - now) / 1000));
  const days = Math.floor(remaining / 86400);
  remaining -= days * 86400;
  const hours = Math.floor(remaining / 3600) % 24;
  remaining -= hours * 3600;
  const minutes = Math.floor(remaining / 60) % 60;
  const seconds = remaining % 60;

  const flips = refreshFlips(timer);
  writeFlip(flips.days, days);
  writeFlip(flips.hours, hours);
  writeFlip(flips.minutes, minutes);
  writeFlip(flips.seconds, seconds);
}

function syncSectionVisibility(timer, startDate, endDate) {
  const section = timer.closest(".shopify-section");
  if (!section) return;

  const now = Date.now();
  if (startDate && startDate.getTime() > now) {
    section.style.display = "none";
    return false;
  }

  if (endDate && endDate.getTime() <= now) {
    return false;
  }

  if (section.style.display === "none") {
    section.style.display = "";
  }
  return true;
}

function enhanceTimer(timer) {
  if (!(timer instanceof CountdownTimer) || patched.has(timer)) return;
  patched.add(timer);

  const expiresAt = parseDate(timer.getAttribute("expires-at"));
  const startsAt = parseDate(timer.getAttribute("starts-at"));
  if (startsAt) startDates.set(timer, startsAt);
  if (!expiresAt) return;

  const tick = () => {
    const startDate = startDates.get(timer);
    const visible = syncSectionVisibility(timer, startDate, expiresAt);
    if (!visible) return;
    applyRemaining(timer, expiresAt);
  };

  // Force an immediate paint with real values (Prestige waits on inView + starts at 00).
  const run = () => {
    tick();
    if (!timer.__rsCountdownInterval) {
      timer.__rsCountdownInterval = window.setInterval(tick, 1000);
    }
  };

  if (customElements.get("countdown-timer-flip")) {
    run();
  } else {
    customElements.whenDefined("countdown-timer-flip").then(run);
  }

  timer.addEventListener(
    "rs:countdown-disconnect",
    () => {
      window.clearInterval(timer.__rsCountdownInterval);
      timer.__rsCountdownInterval = null;
    },
    { once: true }
  );
}

// Safer flip updates (skip no-ops / missing shadow children).
const originalUpdateValue = CountdownTimerFlip.prototype.updateValue;
CountdownTimerFlip.prototype.updateValue = function updateValue(value) {
  if (!this.shadowRoot) {
    return originalUpdateValue.call(this, value);
  }
  const newValue = pad(value);
  [...newValue].forEach((digit, index) => {
    const child = this.shadowRoot.children[index];
    if (!child) return;
    if (child.getAttribute("number") === digit) return;
    child.setAttribute("number", digit);
  });
};

const originalConnected = CountdownTimer.prototype.connectedCallback;
CountdownTimer.prototype.connectedCallback = function connectedCallback() {
  originalConnected.call(this);
  enhanceTimer(this);
};

const originalDisconnected = CountdownTimer.prototype.disconnectedCallback;
CountdownTimer.prototype.disconnectedCallback = function disconnectedCallback() {
  this.dispatchEvent(new CustomEvent("rs:countdown-disconnect"));
  window.clearInterval(this.__rsCountdownInterval);
  this.__rsCountdownInterval = null;
  originalDisconnected.call(this);
};

function enhanceAll() {
  document.querySelectorAll("countdown-timer").forEach((timer) => enhanceTimer(timer));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", enhanceAll, { once: true });
} else {
  enhanceAll();
}

document.addEventListener("shopify:section:load", enhanceAll);
